import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { get } from "../../../api/client";
import type { SiteRegister } from "../../../api/sites";
import type { Candidate, JobTarget, WorkOrderRead } from "./readerApi";

interface Props {
  candidates: Candidate[];
  supplier: string;
  /** The site the quotation itself names, to start the search from. */
  quotedSite: string;
  value: JobTarget | null;
  onChange: (target: JobTarget) => void;
  /** The work order the quotation states, and the sites under it. */
  workOrder: WorkOrderRead | null;
  /** False while the job is the one read from the quote and not yet confirmed. */
  confirmed: boolean;
  onConfirm: () => void;
}

/** Which job this quotation prices: a submitted inspection waiting for it, or
 * -- when nobody has visited yet -- the site it is for. An inspection is not
 * required first: allocated to a site, the quotation joins that site's open
 * job, or opens one under the site's work order number for the crew to
 * complete later.
 *
 * The job the quotation points at -- by the work order it states, or the site
 * it names -- is pre-selected, and held until the office confirms it or picks
 * another: a suggestion, never a decision. */
export default function InspectionPicker({ candidates, supplier, quotedSite, value, onChange, workOrder, confirmed, onConfirm }: Props) {
  return (
    <div className="qr-job">
      <FromQuote workOrder={workOrder} value={value} confirmed={confirmed} onConfirm={onConfirm} />
      {candidates.length === 0 ? (
        <p className="notice">
          No submitted inspection is waiting for {supplier}&rsquo;s quotation. Allocate it to its site below: the
          checklist can be completed on the same job later.
        </p>
      ) : (
        <div className="qr-candidates" role="radiogroup" aria-label="The inspection this quotation prices">
          {candidates.map((candidate) => {
            const on = value?.kind === "inspection" && value.id === candidate.inspection_id;
            return (
              <label key={candidate.inspection_id} className={on ? "qr-candidate on" : "qr-candidate"}>
                <input
                  type="radio"
                  name="job"
                  checked={on}
                  onChange={() => onChange({ kind: "inspection", id: candidate.inspection_id })}
                />
                <span className="qr-candidate-text">
                  <strong>{candidate.site || "Site not named"}</strong>
                  <span>Works order {candidate.work_order_no}</span>
                </span>
                {candidate.by_work_order ? (
                  <span className="status-chip tone-ok">Matches the work order on the quote</span>
                ) : candidate.suggested ? (
                  <span className="status-chip tone-ok">Matches the site on the quote</span>
                ) : null}
              </label>
            );
          })}
        </div>
      )}
      <SiteSearch
        startWith={workOrder?.number ?? quotedSite}
        value={value}
        onChange={onChange}
        hasCandidates={candidates.length > 0}
      />
    </div>
  );
}

/** What the quote said about its job, and -- while the job pre-selected from
 * it is unconfirmed -- the button that confirms it. Changing the selection
 * below, or the number in the search, is the other way on. */
function FromQuote({
  workOrder,
  value,
  confirmed,
  onConfirm,
}: {
  workOrder: WorkOrderRead | null;
  value: JobTarget | null;
  confirmed: boolean;
  onConfirm: () => void;
}) {
  const pending = value !== null && !confirmed;
  if (workOrder === null && !pending) {
    return null;
  }
  const sites = workOrder?.sites ?? [];
  const where =
    sites.length === 1
      ? ` · ${sites[0].name}${sites[0].cluster ? ` (${sites[0].cluster})` : ""}`
      : sites.length > 1
        ? ` · ${sites.length} sites share this number, pick the right one below`
        : " · no site in the register has this number, search for the site below";
  return (
    <section className="qr-from-quote" aria-label="Read from the quote">
      {workOrder !== null ? (
        <p>
          Work order on the quote: <strong className="mono">{workOrder.number}</strong>
          {where}
        </p>
      ) : (
        <p>Suggested from the site the quote names.</p>
      )}
      {pending ? (
        <div className="qr-from-quote-confirm">
          <button type="button" className="btn btn-primary" onClick={onConfirm}>
            Confirm this {value.kind === "site" ? "site" : "inspection"}
          </button>
          <span className="muted">or choose another below. Change the number in the search to look up a different work order.</span>
        </div>
      ) : value !== null ? (
        <span className="status-chip tone-ok">Confirmed</span>
      ) : null}
    </section>
  );
}

const MIN_QUERY = 2;

function workOrderOf(site: SiteRegister["rows"][number]): string | null {
  return site.notif_no ?? site.rs_no;
}

function SiteSearch({
  startWith,
  value,
  onChange,
  hasCandidates,
}: {
  /** The work order the quote states, else the site it names. */
  startWith: string;
  value: JobTarget | null;
  onChange: (target: JobTarget) => void;
  hasCandidates: boolean;
}) {
  const [typed, setTyped] = useState(startWith);
  const [query, setQuery] = useState(startWith.trim());

  // Searched once the typing pauses, as the Sites register searches.
  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(typed.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [typed]);

  const found = useQuery<SiteRegister, Error>({
    queryKey: ["quotation-reader", "site-search", query],
    queryFn: () => get<SiteRegister>(`/api/v1/sites?q=${encodeURIComponent(query)}&limit=8`),
    enabled: query.length >= MIN_QUERY,
  });
  const rows = found.data?.rows ?? [];

  return (
    <section className="qr-site-search" aria-label="Allocate to a site">
      <label className="qr-site-search-field">
        <span>{hasCandidates ? "Or allocate it to a site" : "Allocate it to a site"}</span>
        <input
          type="search"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder="Search by site name, address or work order number"
        />
      </label>
      {query.length < MIN_QUERY ? null : found.isError ? (
        <p className="notice bad" role="alert">
          Could not search the sites: {found.error.message}
        </p>
      ) : found.isPending ? (
        <p className="muted">Searching…</p>
      ) : rows.length === 0 ? (
        <p className="muted">No site matches &ldquo;{query}&rdquo;.</p>
      ) : (
        <div className="qr-candidates" role="radiogroup" aria-label="The site this quotation is for">
          {rows.map((site) => {
            const on = value?.kind === "site" && value.id === site.id;
            const workOrder = workOrderOf(site);
            return (
              <label key={site.id} className={on ? "qr-candidate on" : "qr-candidate"}>
                <input
                  type="radio"
                  name="job"
                  checked={on}
                  onChange={() => onChange({ kind: "site", id: site.id, name: site.name, workOrder })}
                />
                <span className="qr-candidate-text">
                  <strong>{site.name}</strong>
                  <span>
                    {workOrder ? `Work order ${workOrder}` : "No work order number"}
                    {site.cluster ? ` · ${site.cluster.name}` : ""}
                  </span>
                </span>
                {site.priority !== null ? <span className="site-red-badge">Red List #{site.priority}</span> : null}
              </label>
            );
          })}
        </div>
      )}
    </section>
  );
}
